/**
 * Provenance strip — who is behind the catalogue.
 *
 * House style is the catalogue's (see app/pages/Datasets/ui.tsx and
 * Landing/ContactSection.tsx): monospace labels, hairline borders on the flat
 * near-black ground, the section label notched into the top border, near-square
 * corners, and no transitions or animation at all.
 *
 * EVERY CLAIM HERE IS VERIFIABLE, and the file is deliberately narrow about it:
 *
 *  - Affiliations are stated as `institution + dates + title` and nothing more.
 *    No superlatives, no "leading"/"world-class". The institution names are the
 *    signal; adjectives would only dilute them.
 *  - There is NO client claim of any kind — no counts, no logos, no "trusted
 *    by". The download and star figures below are for Derek's open-source
 *    PACKAGES and are labelled as such; they must never be re-worded into
 *    anything that implies Sov.ai customers.
 *  - The company line is "infrastructure started 2021, first client deliveries
 *    2023", which is the wording used in the FAQ (app/pages/FAQ/index.tsx).
 *    Do not replace it with a founding year — an "Established 20xx" line that
 *    contradicts the FAQ and the founder's CV is exactly what diligence catches.
 *  - Links are only rendered where the exact URL is known (the two GitHub
 *    repositories, both verified to resolve). The SSRN papers are plain text
 *    because a guessed SSRN abstract id would be worse than no link.
 *
 * Per-repo star counts are intentionally omitted: they drift daily, and a
 * hard-coded number goes stale the week after it is written. The two aggregate
 * figures are stated as floors ("300,000+", "24,000+") for the same reason.
 */
import React from 'react';

/** Page ground. Repeated on the notch label so it interrupts the hairline. */
const GROUND = 'bg-[#0d1117]';

const LABEL = 'font-mono text-[11px] uppercase tracking-wider text-gray-400';

/**
 * Links are blue-400 (~6.9:1 on the ground), matching ContactSection. Focus is
 * an instant 1px outline — nothing here eases, so there is nothing for
 * prefers-reduced-motion to suppress.
 */
const LINK =
  'text-blue-400 underline underline-offset-2 hover:text-blue-300 ' +
  'focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-blue-400';

/**
 * One appointment. `when` is mono and tabular so the dates form a column the
 * eye can scan without reading the institution names.
 */
function Post({
  when,
  where,
  what,
}: {
  when: string;
  where: string;
  what: string;
}) {
  return (
    <li className="border-b border-gray-800 py-1.5 last:border-b-0 sm:flex sm:items-baseline sm:gap-x-3">
      {/*
        Narrow: the date sits inline with the institution and the title wraps
        under it — two lines per appointment rather than three, which is worth
        ~70px of page height across the four rows.
      */}
      <span className="mr-3 shrink-0 font-mono text-[11px] tabular-nums text-gray-400 sm:mr-0 sm:w-24">
        {when}
      </span>
      <span className="font-mono text-[13px] leading-snug text-white">
        {where}
      </span>
      <span className="block text-[12px] leading-snug text-gray-400 sm:ml-auto sm:text-right">
        {what}
      </span>
    </li>
  );
}

/** Plain key/value pair — the same strip ContactSection uses for its details. */
function Fact({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b border-gray-800 px-3 py-2 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0">
      <div className={LABEL}>{label}</div>
      <div className="mt-0.5 text-[12px] leading-snug text-gray-200">
        {children}
      </div>
    </div>
  );
}

export const Provenance: React.FC = () => {
  return (
    <section id="provenance" className={`w-full ${GROUND} py-8`}>
      <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="relative border border-gray-800 px-4 pb-3 pt-5 sm:px-5">
          <span
            className={`absolute -top-[7px] left-3 ${GROUND} px-1.5 ${LABEL}`}
          >
            provenance
          </span>

          {/* Who, and what he currently does. One line, no biography. */}
          <div className="flex flex-col gap-x-4 gap-y-0.5 pb-2 sm:flex-row sm:items-baseline">
            <h2 className="font-mono text-base font-semibold text-white">
              Derek Snow
            </h2>
            {/*
              The current title is carried by the first row of the list below,
              so this line only adds what the list cannot: what he teaches.
            */}
            <p className="text-[12px] leading-snug text-gray-400">
              Teaches FRE-GY 7773, Machine Learning in Financial Engineering, at
              New York University.
            </p>
          </div>

          {/* Appointments, most recent first. Dates and titles only. */}
          <ul className="border-t border-gray-800">
            <Post
              when="2022–"
              where="New York University"
              what="Assistant Industry Professor, Finance & Risk Engineering"
            />
            <Post
              when="2021–22"
              where="Oxford-Man Institute"
              what="Associate Member, University of Oxford"
            />
            <Post
              when="2020–21"
              where="The Alan Turing Institute"
              what="Research Associate, Finance & Economics Programme"
            />
            <Post
              when="2018–19"
              where="Cambridge Centre for Alternative Finance"
              what="Research Assistant, University of Cambridge"
            />
          </ul>

          {/*
            Open source, publications and the company timeline as a data strip.
            The open-source figures are explicitly labelled as package figures.
          */}
          <div className="mt-3 grid grid-cols-1 border border-gray-800 sm:grid-cols-3">
            <Fact label="open source">
              300,000+ package downloads, 24,000+ GitHub stars across{' '}
              <a
                href="https://github.com/firmai/financial-machine-learning"
                target="_blank"
                rel="noopener noreferrer"
                className={LINK}
              >
                financial-machine-learning
              </a>{' '}
              and{' '}
              <a
                href="https://github.com/firmai/industry-machine-learning"
                target="_blank"
                rel="noopener noreferrer"
                className={LINK}
              >
                industry-machine-learning
              </a>
              .
            </Fact>

            <Fact label="publications">
              <span className="italic">
                Machine Learning in Asset Management
              </span>{' '}
              (Journal of Financial Data Science).{' '}
              <span className="italic">Black-Box Model Risk in Finance</span>,
              with Samuel N. Cohen and Lukasz Szpruch. Both on SSRN.
            </Fact>

            <Fact label="sov.ai">
              Infrastructure started 2021. First client deliveries 2023.
            </Fact>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Provenance;
